import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Shield, 
  Eye, 
  Lock, 
  Key, 
  FileText, 
  Users, 
  AlertTriangle,
  CheckCircle,
  Settings,
  Database,
  Download,
  Trash2,
  Clock,
  Info,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PrivacidadRGPD() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    auditoria_activa: true,
    retencion_logs_dias: 365,
    cifrado_activo: true,
    doble_factor_obligatorio: false,
    sesion_timeout_minutos: 480,
    ip_whitelist_activa: false,
    notificar_accesos: false,
    exportacion_datos_activa: true,
    derecho_olvido_activo: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Intentar cargar configuración existente
      const token = localStorage.getItem('lexduo_token');
      const response = await axios.get(`${API}/api/configuracion/privacidad`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Si no existe la configuración, usar valores por defecto
      console.log('Usando configuración por defecto');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('lexduo_token');
      await axios.post(`${API}/api/configuracion/privacidad`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Guardado",
        description: "Configuración de privacidad actualizada correctamente"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo guardar la configuración",
        variant: "destructive"
      });
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/configuracion')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Privacidad y Seguridad RGPD
          </h1>
          <p className="text-gray-500">Configuración de cumplimiento normativo RGPD/LOPDGDD</p>
        </div>
      </div>

      {/* Alert informativo */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Esta configuración ayuda a cumplir con el Reglamento General de Protección de Datos (RGPD) 
          y la Ley Orgánica de Protección de Datos y Garantía de Derechos Digitales (LOPDGDD).
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="seguridad" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="seguridad">Seguridad</TabsTrigger>
          <TabsTrigger value="auditoria">Auditoría</TabsTrigger>
          <TabsTrigger value="derechos">Derechos RGPD</TabsTrigger>
          <TabsTrigger value="datos">Gestión Datos</TabsTrigger>
        </TabsList>

        {/* Seguridad */}
        <TabsContent value="seguridad" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Configuración de Seguridad
              </CardTitle>
              <CardDescription>Controles de acceso y protección de datos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="font-medium">Cifrado de datos sensibles</Label>
                  <p className="text-sm text-gray-500">Cifrar datos personales en la base de datos</p>
                </div>
                <Switch 
                  checked={settings.cifrado_activo} 
                  onCheckedChange={() => handleToggle('cifrado_activo')}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="font-medium">Doble factor de autenticación obligatorio</Label>
                  <p className="text-sm text-gray-500">Requiere 2FA para todos los usuarios</p>
                </div>
                <Switch 
                  checked={settings.doble_factor_obligatorio} 
                  onCheckedChange={() => handleToggle('doble_factor_obligatorio')}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="font-medium">Lista blanca de IPs</Label>
                  <p className="text-sm text-gray-500">Solo permitir acceso desde IPs autorizadas</p>
                </div>
                <Switch 
                  checked={settings.ip_whitelist_activa} 
                  onCheckedChange={() => handleToggle('ip_whitelist_activa')}
                />
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Timeout de sesión (minutos)</Label>
                <p className="text-sm text-gray-500">Tiempo de inactividad antes de cerrar sesión automáticamente</p>
                <input 
                  type="number" 
                  value={settings.sesion_timeout_minutos}
                  onChange={(e) => setSettings(prev => ({ ...prev, sesion_timeout_minutos: parseInt(e.target.value) || 480 }))}
                  className="w-32 px-3 py-2 border rounded-md"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auditoría */}
        <TabsContent value="auditoria" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Registro de Auditoría
              </CardTitle>
              <CardDescription>Configuración de logs y trazabilidad</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="font-medium">Auditoría activa</Label>
                  <p className="text-sm text-gray-500">Registrar todos los accesos y operaciones</p>
                </div>
                <Switch 
                  checked={settings.auditoria_activa} 
                  onCheckedChange={() => handleToggle('auditoria_activa')}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="font-medium">Notificar accesos sospechosos</Label>
                  <p className="text-sm text-gray-500">Enviar alertas por email ante accesos inusuales</p>
                </div>
                <Switch 
                  checked={settings.notificar_accesos} 
                  onCheckedChange={() => handleToggle('notificar_accesos')}
                />
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Retención de logs (días)</Label>
                <p className="text-sm text-gray-500">Tiempo que se conservan los registros de auditoría</p>
                <input 
                  type="number" 
                  value={settings.retencion_logs_dias}
                  onChange={(e) => setSettings(prev => ({ ...prev, retencion_logs_dias: parseInt(e.target.value) || 365 }))}
                  className="w-32 px-3 py-2 border rounded-md"
                />
              </div>

              <Button variant="outline" onClick={() => navigate('/configuracion/auditoria')}>
                <Eye className="h-4 w-4 mr-2" />
                Ver registros de auditoría
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Derechos RGPD */}
        <TabsContent value="derechos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Derechos de los Interesados
              </CardTitle>
              <CardDescription>Configuración para cumplir con los derechos ARCO</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="font-medium">Derecho de acceso y portabilidad</Label>
                  <p className="text-sm text-gray-500">Permitir exportación de datos personales</p>
                </div>
                <Switch 
                  checked={settings.exportacion_datos_activa} 
                  onCheckedChange={() => handleToggle('exportacion_datos_activa')}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="font-medium">Derecho de supresión (Olvido)</Label>
                  <p className="text-sm text-gray-500">Permitir eliminación completa de datos personales</p>
                </div>
                <Switch 
                  checked={settings.derecho_olvido_activo} 
                  onCheckedChange={() => handleToggle('derecho_olvido_activo')}
                />
              </div>

              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  El derecho de supresión tiene excepciones legales. Los datos relacionados con 
                  expedientes judiciales pueden estar sujetos a períodos de retención obligatorios.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gestión de Datos */}
        <TabsContent value="datos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Gestión de Datos Personales
              </CardTitle>
              <CardDescription>Herramientas para gestión de datos RGPD</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <Download className="h-8 w-8 text-blue-600" />
                    <div>
                      <h4 className="font-medium">Exportar datos</h4>
                      <p className="text-sm text-gray-500">Generar informe de datos personales</p>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full mt-4" onClick={() => navigate('/configuracion/gestion-datos')}>
                    Ir a exportación
                  </Button>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <FileText className="h-8 w-8 text-green-600" />
                    <div>
                      <h4 className="font-medium">Registro de tratamientos</h4>
                      <p className="text-sm text-gray-500">Documentación de actividades</p>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full mt-4">
                    Ver registro
                  </Button>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Botón Guardar */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/configuracion')}>
          Cancelar
        </Button>
        <Button onClick={handleSave}>
          <CheckCircle className="h-4 w-4 mr-2" />
          Guardar configuración
        </Button>
      </div>
    </div>
  );
}
